import clsx from "clsx";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Avatar,
  Badge,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  makeStyles,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  withStyles,
} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import MenuIcon from "@material-ui/icons/Menu";
import BackdropLoading from "../components/BackdropLoading";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import UserModal from "../components/UserModal";
import { AnimatePresence, motion } from "framer-motion";
import { AuthContext } from "../context/Auth/AuthContext";
import MainListItems from "./MainListItems";
import { MoonStar, Sun } from "lucide-react";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import { getBackendUrl } from "../config";
import toastError from "../errors/toastError";
import { useDate } from "../hooks/useDate";
import useSettings from "../hooks/useSettings";
import { i18n } from "../translate/i18n";
import { Link as RouterLink } from "react-router-dom";
import ColorModeContext from "./themeContext";
import { ChevronRight } from "@material-ui/icons";
import AppListItems from "./AppListItems";
import NotificationsVolume from '../components/NotificationsVolume';
import { LogOutIcon } from "lucide-react";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import useVersion from "../hooks/useVersion";
import VersionControl from "../components/VersionControl";
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import AirplanemodeInactiveTwoToneIcon from '@material-ui/icons/AirplanemodeInactiveTwoTone';
import AppsIcon from '@material-ui/icons/Apps';
import PersonIcon from '@material-ui/icons/Person';
import ApartmentIcon from '@material-ui/icons/Apartment';

import {
  AllInclusive,
  AttachFile,
  DeviceHubOutlined,
  PhonelinkSetup,
} from "@material-ui/icons";
import { Ri24HoursFill } from "react-icons/ri";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import { ShapeLine, Webhook } from "@mui/icons-material";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import CodeRoundedIcon from "@material-ui/icons/CodeRounded";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import BusinessIcon from "@material-ui/icons/Business";
import LocalAtmIcon from "@material-ui/icons/LocalAtm";
import ListIcon from "@material-ui/icons/ListAlt";
import PeopleIcon from "@material-ui/icons/People";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import Schedule from "@material-ui/icons/Schedule";
import WarningIcon from '@material-ui/icons/Warning';

const backendUrl = getBackendUrl();
const drawerWidth = 240;
const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    [theme.breakpoints.down("md")]: {
      height: "calc(100vh - 56px)",
    },
    backgroundColor: theme.palette.fancyBackground,
    "& .MuiButton-outlinedPrimary": {
      color: theme.palette.primary,
      border:
        theme.mode === "light"
          ? "1px solid rgba(0 124 102)"
          : "1px solid rgba(187, 51, 51, 0.5)",
    },
    "& .MuiTab-textColorPrimary.Mui-selected": {
      color: theme.palette.primary,
    },
  },
  chip: {
    background: "red",
    color: "white",
  },
  avatar: {
    width: "100%",
  },
  toolbar: {
    paddingRight: 24, // keep right padding when drawer closed
    color: theme.palette.dark.main,
    background: theme.palette.barraSuperior,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // backgroundColor: "#FFF",
    backgroundSize: "cover",
    padding: "0 8px",
    minHeight: "48px",
    [theme.breakpoints.down("sm")]: {
      height: "48px",
    },
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    // [theme.breakpoints.down("sm")]: {
    //   display: "none",
    // },
  },
  // menuButton: {
  //   marginRight: 36,
  // },
  menuButtonHidden: {
    display: "none",
  },
  containerMenuButton: {
    width: "100%",
    height: "37px",
    backgroundColor: "#FAFAFA",
    position: "fixed",
    zIndex: 99,
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
  menuButton: {
    color: "black",
    display: "block",
    marginLeft: 5,
  },
  title: {
    flexGrow: 1,
    fontSize: 14,
    color: "white",
    marginLeft: 10,
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    // overflowX: "hidden",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
    overflowY: "hidden",
  },

  drawerPaperClose: {
    overflowX: "hidden",
    overflowY: "hidden",
    height: "100dvh",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: "72px",
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },

  appBarSpacer: {
    minHeight: "48px",
  },
  appBarSpacerVertical: {
    minHeight: 0,
  },
  content: {
    flex: 1,
    overflow: "auto",
    height: "100dvh",
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  containerWithScroll: {
    flex: 1,
    // padding: theme.spacing(1),
    overflowY: "scroll", // Use "auto" para mostrar a barra de rolagem apenas quando necessário
    overflowX: "hidden", // Oculta a barra de rolagem horizontal
    ...theme.scrollbarStyles,
    borderRadius: "8px",
    border: "2px solid transparent",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    "scrollbar-width": "none",
  },
  NotificationsPopOver: {
    // color: theme.barraSuperior.secondary.main,
  },
  logo: {
    width: "100%",
    height: "45px",
    maxWidth: 180,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      height: "100%",
      maxWidth: 180,
    },
    logo: theme.logo,
    content:
      "url(" +
      (theme.mode === "light"
        ? theme.calculatedLogoLight()
        : theme.calculatedLogoDark()) +
      ")",
  },
  hideLogo: {
    display: "none",
  },
  avatar2: {
    width: "42px",
    height: "42px",
    cursor: "pointer",
    borderRadius: "50%",
    border: "2px solid #ccc",
  },
  updateDiv: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  hideSideBar: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "block",
    },
  },
  hideTopBar: {
    display: "none",
  },
}));

const StyledBadge = withStyles((theme) => ({
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}))(Badge);

const SmallAvatar = withStyles((theme) => ({
  root: {
    width: 22,
    height: 22,
    border: `2px solid ${theme.palette.background.paper}`,
  },
}))(Avatar);

const LoggedInLayout = ({ children, themeToggle }) => {
  const classes = useStyles();
  const [userToken, setUserToken] = useState("disabled");
  const [loadingUserToken, setLoadingUserToken] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, socket, handleLogout, loading, isHorizontal, setIsHorizontal } =
    useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const [dueDate, setDueDate] = useState("");

  const [activeButton, setActiveButton] = useState(null);
  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));
  const [selectedSubMenu, setSelectedSubMenu] = useState("");
  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);
  const { dateToClient } = useDate();
  const [profileUrl, setProfileUrl] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const mainListItems = useMemo(
    () => <MainListItems drawerOpen={drawerOpen} collapsed={!drawerOpen} />,
    [user, drawerOpen]
  );

  const [version, setVersion] = useState(false);
  const { getVersion } = useVersion();

  const { whatsApps } = useContext(WhatsAppsContext);

  const [connectionWarning, setConnectionWarning] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  useEffect(() => {
    async function fetchVersion() {
      const _version = await getVersion();
      setVersion(_version.version);
    }
    fetchVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const settings = useSettings();

  useEffect(() => {
    const getSetting = async () => {
      const response = await settings.get("wtV");

      if (response) {
        setUserToken("disabled");
      } else {
        setUserToken("disabled");
      }
    };

    getSetting();
  });

  useEffect(() => {
    if (localStorage.getItem("public-token") === null) {
      handleLogout(); // Logoff automático caso o token não exista
    }

    if (document.body.offsetWidth > 600) {
      if (user.defaultMenu === "closed") {
        setDrawerOpen(false);
      } else {
        setDrawerOpen(true);
      }
    }

    if (user.defaultTheme === "dark" && theme.mode === "light") {
      colorMode.toggleColorMode();
    }
  }, [user.defaultMenu, document.body.offsetWidth]);


  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen]);

  useEffect(() => {
    const companyId = user.companyId;
    const userId = user.id;
    if (companyId) {
      //    const socket = socketManager.GetSocket();

      const ImageUrl = user.profileImage;
      if (ImageUrl !== undefined && ImageUrl !== null)
        setProfileUrl(
          `${backendUrl}/public/company${companyId}/user/${ImageUrl}`
        );
      else setProfileUrl(`${process.env.FRONTEND_URL}/nopicture.png`);

      const onCompanyAuthLayout = (data) => {
        if (data.user.id === +userId) {
          toastError("Sua conta foi acessada em outro computador.");
          setTimeout(() => {
            localStorage.clear();
            window.location.reload();
          }, 1000);
        }
      };

      socket.on(`company-${companyId}-auth`, onCompanyAuthLayout);

      socket.emit("userStatus");
      const interval = setInterval(() => {
        socket.emit("userStatus");
      }, 1000 * 120); //status do usuário online de 5 para 2 min.

      return () => {
        socket.off(`company-${companyId}-auth`, onCompanyAuthLayout);
        clearInterval(interval);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600 || user.defaultMenu === "closed") {
      setDrawerOpen(false);
    }
  };

  const handleRefreshPage = () => {
    window.location.reload(false);
  };

  if (loading) {
    return <BackdropLoading />;
  }

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveButton(null);
  };

  const toggleDrawer = (open) => (event) => {
    // Verifica se o clique é fora do drawer (não é um clique no próprio drawer ou nas teclas ESC/Enter)
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setIsDrawerOpen(open);
  };

  const handleSubmenuOpen = (event) => {
    setSubmenuAnchorEl(event.currentTarget);
  };

  const handleSubmenuClose = () => {
    setSubmenuAnchorEl(null);
  };

  const handleMenuClick = (event, buttonIndex) => {
    // Abre o menu correspondente e fecha qualquer outro menu aberto
    setAnchorEl(event.currentTarget);
    setActiveButton(buttonIndex);
  };

  const handleSubMenuClick = (submenu, buttonIndex) => {
    // Define o submenu correspondente ao botão e limpa todos os outros
    setSelectedSubMenu({ [buttonIndex]: submenu });
    handleMenuClose();
  };

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        // className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose,
            isHorizontal && greaterThenSm && classes.hideSideBar
          ),
        }}
        open={drawerOpen}
        onClose={() => setDrawerOpen(!drawerOpen)}
      >
        <div className={classes.toolbarIcon}>
          <img
            className={drawerOpen ? classes.logo : classes.hideLogo}
            alt="logo"
          />
          <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
            {!drawerOpen ? <ChevronRight /> : <ChevronLeftIcon />}
          </IconButton>
        </div>
        <List className={classes.containerWithScroll}>
          {/* {mainListItems} esse não */}
          <MainListItems
            collapsed={!drawerOpen}
            isHorizontal={(value) => setIsHorizontal(value)}
          />
        </List>
        <Divider />
      </Drawer>
      <div className={classes.containerMenuButton}>
        <IconButton
          edge="start"
          variant="contained"
          onClick={() => setDrawerOpen(!drawerOpen)}
          aria-label="open drawer"
          className={clsx(
            classes.menuButton,
            !isHorizontal && drawerOpen && classes.menuButtonHidden
          )}
        >
          <MenuIcon />
        </IconButton>
      </div>
      <AppBar
        position="absolute"
        className={clsx(
          classes.appBar,
          // drawerOpen && classes.appBarShift,
          !isHorizontal && greaterThenSm && classes.hideTopBar
        )}
        color="primary"
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <img className={classes.logo} alt="logo" />
          <Typography
            component="h2"
            variant="h6"
            color="inherit"
            noWrap
            className={classes.title}
          >
          </Typography>
          <AppListItems />
          {userToken === "enabled" && user?.companyId === 1 && (
            <Chip
              className={classes.chip}
              label={i18n.t("mainDrawer.appBar.user.token")}
            />
          )}

          <div
            style={{
              marginRight: "5px", // Espaço entre o item e o próximo à direita
            }}
          >
            <VersionControl />
          </div>

          {connectionWarning && (
            <>
              <style>
                {`
        @media (max-width: 1700px) {
          .responsive-text {
            display: none;
          }
        }
      `}
              </style>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    backgroundColor: "red",
                    color: "white",
                    fontSize: "13px",
                    padding: "4px 8px",
                    borderRadius: "50px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <AirplanemodeInactiveTwoToneIcon
                    style={{
                      color: "white",
                      width: "20px",
                      height: "20px",
                    }}
                  />
                  <span className="responsive-text">Verifique as conexões</span>
                </span>
              </div>
            </>
          )}
          <AnnouncementsPopover />



          {/* Menu Setting */}
          {user.profile === "admin" && (
            <Tooltip title={i18n.t("appBarListem.settings")}>
              <IconButton
                color="inherit"
                size="small"
                onClick={(event) => handleMenuClick(event, 1)}
                style={{
                  borderRadius: selectedSubMenu[0] ? 5 : 80,
                  paddingInline: selectedSubMenu[0] ? "5px" : 2,
                  fontSize: "14px",
                  color: "#FFF",
                  fontFamily: "Poppins",
                }}
              >
                <AppsIcon style={{ width: "30px", height: "25px" }} />{""}
                <AnimatePresence>
                  {selectedSubMenu[1] && (
                    <motion.span
                      key="submenu-1"
                      initial={{ width: 0, opacity: 0 }}
                      animate={{ width: "auto", opacity: 1 }}
                      exit={{ width: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      style={{
                        marginLeft: 4,
                        display: "inline-block",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        fontSize: "14px",
                        fontFamily: "Poppins",
                      }}
                    >
                      {selectedSubMenu[1]}
                    </motion.span>
                  )}
                </AnimatePresence>
              </IconButton>
            </Tooltip>
          )}

          <Menu
            anchorEl={anchorEl}
            keepMounted
            getContentAnchorEl={null}
            open={activeButton === 1}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            PaperProps={{
              style: {
                overflow: "hidden", // Remove barras de rolagem
                maxHeight: "auto", // Garante que o tamanho seja ajustado automaticamente
                padding: 0, // Remove qualquer padding extra que possa causar deslocamento
              },
            }}
          >
            {/* Primeira seção */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)", // 4 colunas fixas
                margin: "-5px 5px",
              }}
            >
              {[
                { to: "/NotifyMe", icon: <Ri24HoursFill />, text: "Notificação do usuário" },
                { to: "/quick-messages", icon: <FlashOnIcon />, text: "Respostas Rápidas" },
                { to: "/tags", icon: <LocalOfferIcon />, text: "Tags" },
                { to: "/phrase-lists", icon: <EventAvailableIcon />, text: "Fluxo de campanhas" },
                { to: "/flowbuilders", icon: <ShapeLine />, text: "Fluxo de conversa" },
                { to: "/queue-integration", icon: <DeviceHubOutlined />, text: "Integrações" },
                { to: "/files", icon: <AttachFile />, text: "Lista de arquivos" },
                { to: "/campaigns", icon: <ListIcon />, text: "Ver Campanhas" },
                { to: "/contact-lists", icon: <PeopleIcon />, text: "Contatos campanhas" },
                { to: "/campaigns-config", icon: <SettingsOutlinedIcon />, text: "Configuração campanhas" },

              ].map((item, index) => (
                <MenuItem
                  key={index}
                  component={RouterLink}
                  to={item.to}
                  onClick={() => {
                    handleMenuClose(); // Fecha o menu
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    padding: "8px",
                    width: "auto",
                    maxWidth: "95px",
                    minWidth: "85px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "23px",
                      height: "30px",
                      width: "30px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {item.icon}
                  </div>
                  <span
                    style={{
                      fontSize: "12px",
                      marginTop: "6px",
                      textAlign: "center",
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                      maxWidth: "85px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.text}
                  </span>
                </MenuItem>

              ))}
            </div>

            {/* Linha divisória */}
            <div
              style={{
                borderTop: "1px solid #ccc",
                margin: "5px 25px 5px 25px",
              }}
            ></div>

            {/* Segunda seção */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)", // 4 colunas fixas
                margin: "-5px 5px",
              }}
            >
              {[
                { to: "/settings", icon: <SettingsOutlinedIcon />, text: "Configuração" },
                { to: "/queues", icon: <AccountTreeOutlinedIcon />, text: "Filas e Setores" },
                { to: "/users", icon: <PeopleAltOutlinedIcon />, text: "Usuários" },
                { to: "/connections", icon: <DeviceHubIcon />, text: "Conexões" },
                { to: "/messages-api", icon: <CodeRoundedIcon />, text: "API" },
                { to: "/prompts", icon: <AllInclusive />, text: "ChatGPT" },
                { to: "/financeiro", icon: <LocalAtmIcon />, text: "Financeiro" },
              ]
                .filter(Boolean)
                .map((item, index) => (
                  <MenuItem
                    key={index}
                    component={RouterLink}
                    to={item.to}
                    onClick={() => {
                      handleMenuClose(); // Fecha o menu
                    }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      padding: "8px", // Ajustar padding para balancear o espaçamento
                      width: "auto", // Reduzir largura
                      maxWidth: "95px", // Ajustar a largura máxima
                      minWidth: "85px", // Largura mínima ajustada
                    }}
                  >
                    <div
                      style={{
                        fontSize: "20px", // Ícones com tamanho ajustado
                        height: "30px", // Tamanho adequado para o ícone
                        width: "30px", // Tamanho adequado para o ícone
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {item.icon}
                    </div>
                    <span
                      style={{
                        fontSize: "12px", // Aumentar o texto para maior legibilidade
                        marginTop: "5px", // Ajustar o espaço entre o ícone e o texto
                        textAlign: "center",
                        whiteSpace: "normal", // Permitir quebra de linha
                        wordWrap: "break-word", // Garante a quebra de palavras longas
                        maxWidth: "85px", // Ajuste no tamanho máximo do texto
                        overflow: "hidden", // Evitar texto que ultrapassa
                        textOverflow: "ellipsis", // Caso o texto seja muito longo
                      }}
                    >
                      {item.text}
                    </span>
                  </MenuItem>
                ))}
            </div>

            {/* Linha divisória */}
            {user?.super === true && (
              <div
                style={{
                  borderTop: "1px solid #ccc",
                  margin: "5px 25px 5px 25px",
                }}
              ></div>
            )}


            {/* Terceira seção */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)", // 4 colunas fixas
                margin: "-5px 5px",
              }}
            >
              {[
                user.super && { to: "/allConnections", icon: <PhonelinkSetup />, text: "Gerenciar conexões" },
                user.super && { to: "/companies", icon: <BusinessIcon />, text: "Empresas" },
                user.super && { to: "/announcements", icon: <AnnouncementIcon />, text: "Informativos" },
              ]
                .filter(Boolean)
                .map((item, index) => (
                  <MenuItem
                    key={index}
                    component={RouterLink}
                    to={item.to}
                    onClick={() => {
                      handleMenuClose(); // Fecha o menu
                    }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      padding: "8px", // Ajustar padding para balancear o espaçamento
                      width: "auto", // Reduzir largura
                      maxWidth: "95px", // Ajustar a largura máxima
                      minWidth: "85px", // Largura mínima ajustada
                    }}
                  >
                    <div
                      style={{
                        fontSize: "25px", // Ícones com tamanho ajustado
                        height: "30px", // Tamanho adequado para o ícone
                        width: "30px", // Tamanho adequado para o ícone
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {item.icon}
                    </div>
                    <span
                      style={{
                        fontSize: "12px", // Aumentar o texto para maior legibilidade
                        marginTop: "5px", // Ajustar o espaço entre o ícone e o texto
                        textAlign: "center",
                        whiteSpace: "normal", // Permitir quebra de linha
                        wordWrap: "break-word", // Garante a quebra de palavras longas
                        maxWidth: "85px", // Ajuste no tamanho máximo do texto
                        overflow: "hidden", // Evitar texto que ultrapassa
                        textOverflow: "ellipsis", // Caso o texto seja muito longo
                      }}
                    >
                      {item.text}
                    </span>
                  </MenuItem>
                ))}
            </div>
          </Menu>

          <div>
            <Tooltip title={i18n.t("appBarListem.profile")}>
              <StyledBadge
                overlap="circular"
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                variant="dot"
                onClick={handleMenu}
                style={{ marginLeft: "8px" }} // Espaço de 8px à esquerda
              >
                <Avatar
                  alt="Multi100"
                  className={classes.avatar2}
                  src={profileUrl}
                />
              </StyledBadge>
            </Tooltip>
            <UserModal
              open={userModalOpen}
              onClose={() => setUserModalOpen(false)}
              onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
              userId={user?.id}
            />

            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
              PaperProps={{
                style: {
                  minWidth: "250px", // Define largura mínima fixa
                  maxWidth: "350px", // Define largura máxima fixa
                  overflow: "hidden", // Evita barras de rolagem
                },
              }}
            >

              <Typography
                style={{
                  display: "flex", // Define o contêiner como flexbox
                  alignItems: "center", // Alinha verticalmente no centro
                  justifyContent: "center", // Alinha horizontalmente no centro
                  margin: "4px", // Afasta o texto das bordas do contêiner
                  gap: "3px", // Adiciona espaço uniforme entre os elementos filhos
                }}
              >
                <span style={{ marginRight: "4px" }}>Claro</span>
                <Sun
                  style={{
                    color: theme.palette.dark.main,
                    width: "20px",
                    height: "20px",
                    marginRight: "4px", // Espaço à direita do ícone
                  }}
                />
                <div
                  onClick={colorMode.toggleColorMode}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "50px",
                    height: "25px",
                    borderRadius: "25px",
                    backgroundColor: theme.mode === "dark" ? "#333" : "#ccc",
                    position: "relative",
                    cursor: "pointer",
                    transition: "background-color 0.3s ease",
                    marginRight: "4px", // Espaço à direita do botão
                  }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      backgroundColor: theme.mode === "dark" ? "#f5a623" : "#f1f1f1",
                      position: "absolute",
                      top: "2.5px",
                      left: theme.mode === "dark" ? "25px" : "5px", // Movimenta entre os lados
                      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                      transition: "left 0.3s ease",
                    }}
                  />
                </div>
                <MoonStar
                  style={{
                    color: theme.palette.dark.main,
                    width: "20px",
                    height: "20px",
                    marginRight: "4px", // Espaço à direita do ícone
                  }}
                />
                <span>Escuro</span>
              </Typography>

              <NotificationsVolume setVolume={setVolume} volume={volume} />
              <Divider style={{ margin: "5px 25px 5px 25px" }} />
              <MenuItem onClick={handleOpenUserModal} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {<PeopleAltOutlinedIcon />}
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <Divider style={{ margin: "5px 25px 5px 25px" }} />
              <MenuItem onClick={handleClickLogout} style={{ display: "auto", alignItems: "center", gap: "8px" }}>
                <LogOutIcon />
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
              <Divider style={{ margin: "5px 25px 5px 25px" }} />
              <Typography
                style={{
                  display: "flex", // Define o contêiner como flexbox
                  justifyContent: "center", // Centraliza horizontalmente
                  alignItems: "center", // Centraliza verticalmente
                  gap: "4px", // Espaço entre elementos (se houver outros)
                  margin: "15px 35px 5px 35px", // Afasta o texto das bordas do contêiner
                  fontSize: "12px",
                }}>
                <ApartmentIcon style={{ fontSize: "20px" }} />
                {(user?.company?.name)}
              </Typography>
              <Typography
                style={{
                  display: "flex", // Define o contêiner como flexbox
                  justifyContent: "center", // Centraliza horizontalmente
                  alignItems: "center", // Centraliza verticalmente
                  gap: "8px", // Espaço entre o ícone e o texto
                  margin: "5px 35px 5px 35px", // Afasta o texto das bordas do contêiner
                  fontSize: "12px",
                }}
              >
                <PersonIcon style={{ fontSize: "20px" }} />
                {user.name}
              </Typography>
              <Divider style={{ margin: "5px 25px 5px 25px" }} />
              <Typography
                style={{
                  display: "flex",
                  fontSize: "12px",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  gap: "4px",
                  margin: "5px 35px 5px 35px",
                }}
              >
                {greaterThenSm &&
                  user?.profile === "admin" &&
                  user?.company?.dueDate ? (
                  <>
                    Expiração do plano: <b>{dateToClient(user.company.dueDate)}</b>
                  </>
                ) : null}
              </Typography>
              <Divider style={{ margin: "5px 25px 5px 25px" }} />
              <Typography>
                <React.Fragment>
                  <Typography
                    style={{
                      fontSize: "10px",
                      padding: "10px",
                      textAlign: "center",
                      fontWeight: "bold",
                    }}
                  >
                    Versão: {`${version}`}
                  </Typography>
                </React.Fragment>
              </Typography>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div
          className={
            isHorizontal ? classes.appBarSpacer : classes.appBarSpacerVertical
          }
        />

        {children ? children : null}
      </main>
    </div>
  );
};

export default LoggedInLayout;